import { useSubmit } from "@hyper-fetch/react";
import { useCallback } from "react";
import { toast } from "sonner";

import { client } from "@/api/client";
import { sdk } from "@/api/sdk";
import { m } from "@/paraglide/messages.js";

/** Every booking route and the schedule, requests, and client views that read them. */
export const BOOKING_CACHE_PATTERN = /\/booking/;

/** What every booking change returns: enough to show the result and offer Undo. */
export interface BookingChangeResult {
  activityId: string;
  undoableUntil: string;
}

/** What creating an event returns: enough to take the whole thing back, seats included. */
export interface EventCreatedResult {
  id: string;
  undoableUntil: string;
}

/**
 * Announces a booking change with an Undo button for as long as the server still holds the
 * notifications back. Undo reverts the change before anyone gets an email, so there is no
 * "are you sure" anywhere in the booking UI.
 */
export function useBookingChange() {
  const { submit: undo } = useSubmit(sdk.booking.undo.$activityId.$post);
  const { submit: undoEvent } = useSubmit(sdk.booking.events.$eventId.undo.$post);

  const refresh = useCallback(() => {
    client.cache.invalidate(BOOKING_CACHE_PATTERN);
  }, []);

  /** The toast, with Undo for as long as `undoableUntil` is still ahead. `revert` says if it worked. */
  const announceWithUndo = useCallback(
    ({
      undoableUntil,
      message,
      description,
      revert,
    }: {
      undoableUntil: string;
      message: string;
      description?: string;
      revert: () => Promise<boolean>;
    }) => {
      refresh();
      const remaining = new Date(undoableUntil).getTime() - Date.now();
      if (remaining <= 500) {
        if (description) toast.success(message, { description });
        else toast.success(message);
        return;
      }
      toast.success(message, {
        ...(description ? { description } : {}),
        duration: remaining,
        action: {
          label: m.booking_undo(),
          onClick: async () => {
            if (!(await revert())) {
              toast.error(m.booking_undo_failed());
              return;
            }
            refresh();
            toast.success(m.booking_undo_done());
          },
        },
      });
    },
    [refresh],
  );

  const announce = useCallback(
    (result: BookingChangeResult, message: string, description?: string) =>
      announceWithUndo({
        undoableUntil: result.undoableUntil,
        message,
        description,
        revert: async () => !(await undo({ params: { activityId: result.activityId } })).error,
      }),
    [announceWithUndo, undo],
  );

  /** A group session just booked: Undo removes the event and every seat before any invite goes out. */
  const announceEvent = useCallback(
    (result: EventCreatedResult, message: string, description?: string) =>
      announceWithUndo({
        undoableUntil: result.undoableUntil,
        message,
        description,
        revert: async () => !(await undoEvent({ params: { eventId: result.id } })).error,
      }),
    [announceWithUndo, undoEvent],
  );

  return { announce, announceEvent, refresh };
}
